import "./Pricelist.scss";

import { EuiFlexItem } from "@elastic/eui";
import { PriceLegendNew } from "components/LegendNew";
import { PriceRowNew } from "components/PriceRowNew";
import { PriceTableHeaderNew } from "components/PriceTableHeaderNew";
import React from "react";
import { useParams } from "react-router";

import { useFetchPricelistPrices } from "../api/react-query/queries/pricelist-show";
import { useGetScreenByIdQuery } from "../api/react-query/queries/screens";
import { BannerAll } from "../components/BannerAll";
import { BottomBar } from "../components/BottomBar";
import { PriceLegend } from "../components/Legend";
import { getShopStyle } from "../utils/PricelistUtils";
import { JsonPrice, Price } from "../utils/types";
import { getCategoryNameFromPrices } from "../utils/Utils";

interface PricelistCategorySection {
    category_pricelist_column: number;
    category_pricelist_row: number;
    category_name: string;
    prices: Price[];
}

interface Categories {
    [key: string]: PricelistCategorySection[];
}

const PriceListMultiScreen = () => {
    const { shopId, orientation, screenId } = useParams();
    const defaultColumnOrder = ["g", "j", "st"];
    const { data: pricesData } = useFetchPricelistPrices(shopId, screenId);
    const { screen } = useGetScreenByIdQuery(screenId);
    const styles = getShopStyle({ shopId, orientation, screenId, extraConfig: screen?.extra_config });

    const getFirstNotEmptyCategory = (categories: Price[][]) => {
        for (let category of Object.values(categories)) {
            if (category.length > 0) {
                return category[0].category_name;
            }
        }
    };

    let uniqueCategories: PricelistCategorySection[] = [];

    let categories: Categories = {};

    for (const price of pricesData ?? []) {
        if (
            (price.active && price.kind_id !== null && !window.location.pathname.includes("extra")) ||
            (price.active && window.location.pathname.includes("extra")) ||
            (price.active && screenId != null)
        ) {
            if (!uniqueCategories.some((category) => category.category_name === price.category_name)) {
                uniqueCategories.push({
                    category_pricelist_column: price.category_pricelist_column,
                    category_pricelist_row: price.category_pricelist_row,
                    category_name: price.category_name,
                    prices: [],
                });
            }

            const foundCategory = uniqueCategories.find((category) => category.category_name === price.category_name);
            foundCategory && foundCategory.prices.push(price);
        }
    }

    //Sort unique categories into left and right
    for (const category of uniqueCategories) {
        if (!categories[category.category_pricelist_column]) {
            categories[category.category_pricelist_column] = [];
        }
        categories[category.category_pricelist_column].push(category);
        uniqueCategories = uniqueCategories.filter((cat) => cat.category_name !== category.category_name);
    }

    //Finally sort by row numberc
    const sortByTableIndexAndName = (arr: PricelistCategorySection[]) => {
        return arr.sort((a, b) => {
            if (a.category_pricelist_row === null && b.category_pricelist_row === null) {
                return a.category_name.localeCompare(b.category_name);
            } else if (a.category_pricelist_row === null) {
                return 1;
            } else if (b.category_pricelist_row === null) {
                return -1;
            } else {
                return a.category_pricelist_row - b.category_pricelist_row;
            }
        });
    };

    const extractUniqueLabels = (categories: any[]): string[] => {
        const allLabels = categories.flatMap((cat) =>
            cat.prices.flatMap((price: Price) =>
                price.json_prices ? price.json_prices.map((jp: JsonPrice) => jp.label) : ["Stuk"]
            )
        );
        const uniqueLabels = Array.from(new Set(allLabels));
        return uniqueLabels;
    };

    const getCustomColumnNames = (shopId: string): Record<string, string> => {
        const customMappings: Record<string, Record<string, string>> = {
            "bbc0cfa7-e230-4338-817a-55190debdac0": {},
            "a08a13e2-a31b-4b6d-b2b4-0491cb3d2227": {},
            "ddd70853-5e9d-45ff-82b3-24c9d81d4e32": {},
            "19149768-691c-40d8-a08e-fe900fd23bc0": {},
        };

        return customMappings[shopId] || {};
    };

    const shopColumnNames = getCustomColumnNames(shopId);
    let menus: Price[][][] = [];
    let colsNames: string[][] = [];

    for (let value of Object.values(categories)) {
        value = sortByTableIndexAndName(value);
        menus.push(value.length > 0 ? value.map((price) => price.prices) : []);
        colsNames.push(extractUniqueLabels(value).map((label) => shopColumnNames[label] || label));
    }

    const sortBySuffixOrder = (labels: string[]): string[] => {
        // Helper functions to extract unit from labels (5g returns g, 1j returns j)
        const getUnit = (key: string) => key.match(/[a-zA-Z]+$/)?.[0] || "";

        const getNumber = (key: string) => {
            const match = key.match(/[\d.,]+/); // grab first number part
            if (!match) return 0.0;
            return parseFloat(match[0].replace(",", "."));
        };

        return [...labels].sort((a, b) => {
            // Extract units from both labels
            const unitA = getUnit(a);
            const unitB = getUnit(b);

            // Check in defaultColumnOrder which unit comes first
            const unitIndexA = defaultColumnOrder.indexOf(unitA);
            const unitIndexB = defaultColumnOrder.indexOf(unitB);

            // Sort by their index in the order of the defaultColumnOrder array
            if (unitIndexA !== unitIndexB) {
                return unitIndexA - unitIndexB;
            }

            // If the units are the same (both g or both j), sort by their numeric value
            return getNumber(a) - getNumber(b);
        });
    };

    const Categories = ({ categories }: { categories: Price[][] }) => {
        return (
            <>
                {Object.values(categories).map((prices) => {
                    if (prices.length > 0) {
                        const cols: string[] = [];

                        prices.forEach((price) => {
                            if (price.json_prices) {
                                price.json_prices.forEach((jp) => {
                                    if (shopColumnNames[jp.label]) {
                                        jp.label = shopColumnNames[jp.label];
                                    }
                                    if (!cols.includes(jp.label)) {
                                        cols.push(jp.label);
                                    }
                                });
                            } else {
                                if (!cols.includes("Stuk")) {
                                    cols.push("Stuk");
                                }
                            }
                        });

                        const sortedCols = sortBySuffixOrder(cols);

                        return (
                            <div className="fullCategory" key={getCategoryNameFromPrices(prices)}>
                                <PriceTableHeaderNew
                                    title={getCategoryNameFromPrices(prices)}
                                    cols={sortedCols}
                                    isMultiScreen={Boolean(screenId)}
                                    topHeader={
                                        getFirstNotEmptyCategory(categories) === getCategoryNameFromPrices(prices)
                                    }
                                    image={prices[0].category_image_2}
                                    enableImageForCategory={
                                        prices[0].category_screen_display_options?.enable_image_for_category === true
                                    }
                                    shopId={shopId} //TODO Remove
                                />
                                <div className="backgroundShadow">
                                    <PriceRowNew prices={prices} cols={sortedCols} shopId={shopId} />
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
            </>
        );
    };

    let gridTemplateColumns = "";
    switch (menus.length) {
        case 2:
            gridTemplateColumns = "50.5% 50.5%";
            break;
        case 3:
            gridTemplateColumns = "33.4% 33.4% 33.4%";
            break;
        case 4:
            gridTemplateColumns = "25% 25% 25% 25%";
            break;
        case 5:
            gridTemplateColumns = "20% 20% 20% 20% 20%";
            break;
        case 6:
            gridTemplateColumns = "16.66% 16.66% 16.66% 16.66% 16.66% 16.66%";
            break;
        default:
            gridTemplateColumns = "101.5%";
            break;
    }

    return (
        <EuiFlexItem css={styles}>
            <div className={window.location.pathname.includes("extra") ? "horecapagebackground" : "pagebackground"}>
                <BottomBar />
                <BannerAll shopId={shopId} />
                <div className="pattern" />
                <embed className="logo" src="/images/pricelist/logo.png" />
                <div className="menukaart" style={{ gridTemplateColumns: gridTemplateColumns }}>
                    {menus.map((menu, index) => (
                        <div className="menu">
                            <div className="marginTopTitle">
                                <Categories categories={menu} colsName={colsNames[index]} />
                                {index === menus.length - 1 && <PriceLegend shopId={shopId} />}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <PriceLegendNew shopId={shopId} />
        </EuiFlexItem>
    );
};

export default PriceListMultiScreen;
